// @ts-nocheck

import React, { useLayoutEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { type Point2D } from '../../../types/point';
import { get3PointsBezierCurve } from '../../../utils/maths';
import { ODDS_TRAVEL_ANIMATION_DURATION } from '../constants/oddsTravel';

const getTranslationAnim = (from, to) => {
  const xJump = 50;
  const yJump = -100;

  const getXBezierCoord = get3PointsBezierCurve(0, xJump, to.x - from.x);
  const getYBezierCoord = get3PointsBezierCurve(0, yJump, to.y - from.y);

  const getTransform = (i) => {
    const t = i / 100;

    const x = getXBezierCoord(t);
    const y = getYBezierCoord(t);

    return `transform: translate(${x}px, ${y}px);`;
  };

  const nbKeyframes = 10;
  const chunkSize = Math.floor(100 / nbKeyframes);

  // Dynamic computation of keyframes
  return keyframes`
    ${[...new Array(nbKeyframes).keys()].map((i) => `${(i + 1) * chunkSize}% { ${getTransform((i + 1) * chunkSize)} }`)}
  `;
};

const dropShadowAnim = keyframes`
  0% {
    filter: drop-shadow(0 0 0 black);
  }

  33% {
    filter: drop-shadow(0 0 1em black);
  }

  100% {
    filter: drop-shadow(0 0 0 black);
  }
`;

const opacityAnim = keyframes`
  0% {
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  80% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
`;

const scaleAnim = keyframes`
  0% {
    transform: scale(0);
  }

  10% {
    transform: scale(1);
  }

  50% {
    transform: scale(2);
  }

  90% {
    transform: scale(1);
  }

  100% {
    transform: scale(0);
  }
`;

const getRotationAnim = (isSpecial: boolean) => keyframes`
  100% {
    transform: rotate(${isSpecial ? 45 : 720}deg);
  }
`;

const Wrapper = styled.div`
  position: absolute;
  pointer-events: none;

  ${({ $from }: { $from: Point2D }) => {
    if ($from) {
      return css`
        left: ${$from.x}px;
        top: ${$from.y}px;
        width: 2.5em;
        height: 2.5em;
        transform: translate(-50%, -50%);
      `;
    }
  }}
`;

const transformsSharedStyle = css`
  position: relative;
  width: 100%;
  height: 100%;
`;

const OpacityAnimation = styled.div`
  ${transformsSharedStyle}

  animation: ${opacityAnim} ${ODDS_TRAVEL_ANIMATION_DURATION}ms ease-in-out forwards;
`;

const DropShadowAnimation = styled.div`
  ${transformsSharedStyle}

  will-change: filter;
  animation: ${dropShadowAnim} ${ODDS_TRAVEL_ANIMATION_DURATION}ms linear forwards;
`;

const ScaleAnimation = styled.div`
  ${transformsSharedStyle}

  animation: ${scaleAnim} ${ODDS_TRAVEL_ANIMATION_DURATION}ms ease-in-out forwards;
`;

const TranslationAnimation = styled.div`
  ${transformsSharedStyle}

  ${({ $from, $to }: { $from: Point2D; $to: Point2D }) => {
    if ($from && $to) {
      return css`
        animation: ${getTranslationAnim($from, $to)} ${ODDS_TRAVEL_ANIMATION_DURATION}ms linear forwards;
      `;
    }
  }}
`;

const RotationAnimation = styled.div`
  ${transformsSharedStyle}

  ${({ $isSpecial }: { $isSpecial: boolean }) => css`
    animation: ${getRotationAnim($isSpecial)} ${ODDS_TRAVEL_ANIMATION_DURATION}ms linear forwards;
  `}
`;

const Img = styled.div`
  ${transformsSharedStyle}

  ${({ $iconPath }: { $iconPath: string }) => {
    if ($iconPath) {
      return css`
        background: url('${$iconPath}');
        background-repeat: no-repeat;
        background-position: center center;
        background-size: contain;
      `;
    }
  }}
`;

type OddsTravelAnimationProps = {
  className?: string;
  from: Point2D;
  to: Point2D;
  iconPath: string;
  isSpecial: boolean;
};

const OddsTravelAnimation = ({ className = '', from, to, iconPath, isSpecial }: OddsTravelAnimationProps) => {
  useLayoutEffect(() => {
    const basketNode = document.getElementById(`basket-floating-button`);

    if (basketNode) {
      setTimeout(() => {
        basketNode.style.transition = `transform 150ms ease-in-out`;
        basketNode.style.transform = `scale(1.15)`;

        setTimeout(() => {
          basketNode.style.transform = `scale(1)`;
        }, ODDS_TRAVEL_ANIMATION_DURATION * 0.1);
      }, ODDS_TRAVEL_ANIMATION_DURATION * 0.9);
    }
  }, []);

  return (
    <Wrapper className={className} $from={from}>
      <OpacityAnimation>
        <TranslationAnimation $from={from} $to={to}>
          <ScaleAnimation>
            <DropShadowAnimation>
              <RotationAnimation $isSpecial={isSpecial}>
                <Img $iconPath={iconPath} />
              </RotationAnimation>
            </DropShadowAnimation>
          </ScaleAnimation>
        </TranslationAnimation>
      </OpacityAnimation>
    </Wrapper>
  );
};

export default OddsTravelAnimation;
